import React, { useState, useEffect } from 'react';
import { Occasion } from '../types';

interface ViewOccasionModalProps {
  occasion: Occasion;
  onClose: () => void;
  onEdit?: (occasion: Occasion) => void;
}

const ViewOccasionModal: React.FC<ViewOccasionModalProps> = ({ occasion, onClose, onEdit }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<Occasion>({ ...occasion });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setFormData({ ...occasion });
  }, [occasion]);

  const handleInputChange = (field: keyof Occasion, value: string | number | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    if (onEdit) {
      setLoading(true);
      try {
        await onEdit(formData);
        setIsEditing(false);
      } catch (error) {
        console.error('Error saving occasion:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (amount: number | string) => {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return isNaN(numAmount) ? amount : `${numAmount.toFixed(2)} SAR`;
  };

  const modalStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
    padding: '1rem'
  };

  const contentStyle: React.CSSProperties = {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '2rem',
    width: '100%',
    maxWidth: '800px',
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
  };

  const fieldStyle: React.CSSProperties = {
    marginBottom: '1rem',
    padding: '0.75rem',
    backgroundColor: '#f8f9fa',
    borderRadius: '4px',
    border: '1px solid #e9ecef'
  };

  const labelStyle: React.CSSProperties = {
    fontWeight: '600',
    color: '#495057',
    marginBottom: '0.25rem',
    fontSize: '0.875rem'
  };

  const valueStyle: React.CSSProperties = {
    color: '#212529',
    fontSize: '0.9rem'
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '0.5rem',
    border: '1px solid #ced4da',
    borderRadius: '4px',
    fontSize: '0.9rem'
  };

  const checkboxStyle: React.CSSProperties = {
    marginRight: '0.5rem'
  };

  const isImageField = (field: keyof Occasion) => {
    return field === 'packageImage' || field === 'occasionImage';
  };

  const isImageUrl = (value: any) => {
    if (typeof value !== 'string') return false;
    return value.startsWith('http://') || value.startsWith('https://') || value.startsWith('data:image/');
  };

  const renderField = (label: string, field: keyof Occasion, type: 'text' | 'number' | 'email' | 'tel' | 'date' | 'datetime-local' | 'textarea' | 'checkbox' = 'text') => {
    const value = formData[field];
    
    return (
      <div style={fieldStyle}>
        <div style={labelStyle}>{label}:</div>
        {isEditing ? (
          type === 'checkbox' ? (
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={Boolean(value)}
                onChange={(e) => handleInputChange(field, e.target.checked)}
                style={checkboxStyle}
              />
              {label}
            </label>
          ) : type === 'textarea' ? (
            <textarea
              value={String(value || '')}
              onChange={(e) => handleInputChange(field, e.target.value)}
              style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }}
              rows={3}
            />
          ) : (
            <input
              type={type}
              value={String(value || '')}
              onChange={(e) => handleInputChange(field, type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value)}
              style={inputStyle}
            />
          )
        ) : (
          <div style={valueStyle}>
            {type === 'checkbox' ? (value ? 'Yes' : 'No') : 
             field === 'occasionDate' ? formatDate(String(value)) :
             (field === 'moneyGiftAmount' || field === 'giftPrice' || field === 'deliveryPrice' || field === 'appCommission' || field === 'discount' || field === 'amountForEveryone' || field === 'packagePrice') ? formatCurrency(typeof value === 'number' ? value : typeof value === 'string' ? value : 0) :
             isImageField(field) && isImageUrl(value) ? (
               <div style={{ marginTop: '0.5rem' }}>
                 <img 
                   src={String(value)} 
                   alt={label}
                   style={{ 
                     maxWidth: '200px', 
                     maxHeight: '200px', 
                     objectFit: 'cover',
                     borderRadius: '4px',
                     border: '1px solid #dee2e6',
                     boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                   }}
                   onError={(e) => {
                     const target = e.target as HTMLImageElement;
                     target.style.display = 'none';
                     const parent = target.parentElement;
                     if (parent) {
                       parent.innerHTML = `<div style="color: #6c757d; font-style: italic;">Image not available</div>`;
                     }
                   }}
                 />
                 <div style={{ marginTop: '0.25rem', fontSize: '0.75rem', color: '#6c757d', wordBreak: 'break-all' }}>
                   {String(value)}
                 </div>
               </div>
             ) :
             String(value || 'N/A')}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={modalStyle} onClick={onClose}>
      <div style={contentStyle} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
          <h2 style={{ margin: 0, color: '#333' }}>
            {isEditing ? 'Edit Occasion' : 'View Occasion'}
          </h2>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {!isEditing && onEdit && (
              <button
                onClick={() => setIsEditing(true)}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Edit
              </button>
            )}
            {isEditing && (
              <>
                <button
                  onClick={() => {
                    setIsEditing(false);
                    setFormData({ ...occasion });
                  }}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={loading}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: loading ? '#ccc' : '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: loading ? 'not-allowed' : 'pointer'
                  }}
                >
                  {loading ? 'Saving...' : 'Save'}
                </button>
              </>
            )}
            <button
              onClick={onClose}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Close
            </button>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          {/* Basic Information */}
          <div>
            <h3 style={{ color: '#495057', marginBottom: '1rem', borderBottom: '2px solid #e9ecef', paddingBottom: '0.5rem' }}>
              Basic Information
            </h3>
            {renderField('Occasion Name', 'occasionName', 'text')}
            {renderField('Occasion Type', 'occasionType', 'text')}
            {renderField('Type', 'type', 'text')}
            {renderField('Occasion Date', 'occasionDate', 'datetime-local')}
            {renderField('Note', 'note', 'textarea')}
          </div>

          {/* Person Information */}
          <div>
            <h3 style={{ color: '#495057', marginBottom: '1rem', borderBottom: '2px solid #e9ecef', paddingBottom: '0.5rem' }}>
              Person Information
            </h3>
            {renderField('Person Name', 'personName', 'text')}
            {renderField('Person Email', 'personEmail', 'email')}
            {renderField('Person Phone', 'personPhone', 'tel')}
            {renderField('Person ID', 'personId', 'text')}
            {renderField('Receiver Name', 'receiverName', 'text')}
            {renderField('Receiver Phone', 'receiverPhone', 'tel')}
          </div>

          {/* Gift Information */}
          <div>
            <h3 style={{ color: '#495057', marginBottom: '1rem', borderBottom: '2px solid #e9ecef', paddingBottom: '0.5rem' }}>
              Gift Information
            </h3>
            {renderField('Money Gift Amount', 'moneyGiftAmount', 'number')}
            {renderField('Gift Name', 'giftName', 'text')}
            {renderField('Gift Price', 'giftPrice', 'number')}
            {renderField('Gift Type', 'giftType', 'text')}
            {renderField('Gift Card', 'giftCard', 'text')}
            {renderField('Gift Link', 'giftLink', 'text')}
            {renderField('Package Price', 'packagePrice', 'text')}
            {renderField('Package Image', 'packageImage', 'text')}
            {renderField('Occasion Image', 'occasionImage', 'text')}
          </div>

          {/* Location & Delivery */}
          <div>
            <h3 style={{ color: '#495057', marginBottom: '1rem', borderBottom: '2px solid #e9ecef', paddingBottom: '0.5rem' }}>
              Location & Delivery
            </h3>
            {renderField('City', 'city', 'text')}
            {renderField('District', 'district', 'text')}
            {renderField('Delivery Price', 'deliveryPrice', 'number')}
          </div>

          {/* Financial Information */}
          <div>
            <h3 style={{ color: '#495057', marginBottom: '1rem', borderBottom: '2px solid #e9ecef', paddingBottom: '0.5rem' }}>
              Financial Information
            </h3>
            {renderField('Amount For Everyone', 'amountForEveryone', 'text')}
            {renderField('App Commission', 'appCommission', 'number')}
            {renderField('Discount', 'discount', 'number')}
            {renderField('Bank Name', 'bankName', 'text')}
            {renderField('IBAN Number', 'ibanNumber', 'text')}
          </div>

          {/* Settings */}
          <div>
            <h3 style={{ color: '#495057', marginBottom: '1rem', borderBottom: '2px solid #e9ecef', paddingBottom: '0.5rem' }}>
              Settings
            </h3>
            {renderField('Is Active', 'isActive', 'checkbox')}
            {renderField('Is Private', 'isPrivate', 'checkbox')}
            {renderField('Is For Me', 'isForMe', 'checkbox')}
            {renderField('Is Sharing', 'isSharing', 'checkbox')}
            {renderField('Is Contain Name', 'isContainName', 'checkbox')}
          </div>
        </div>

        {/* Read-only fields */}
        <div style={{ marginTop: '2rem' }}>
          <h3 style={{ color: '#495057', marginBottom: '1rem', borderBottom: '2px solid #e9ecef', paddingBottom: '0.5rem' }}>
            System Information (Read-only)
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            {renderField('Occasion ID', 'occasionId', 'text')}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewOccasionModal;
